import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import messageQueue from '../utils/MessageQueue';
import { getImageUrl } from '../utils/imageUtils';

/**
 * Custom hook for message handling (send, emoji, GIF, file upload)
 */
export const useMessageHandlers = (state, user) => {
  const {
    messageInput, setMessageInput,
    selectedRoom, members,
    replyingTo, setReplyingTo,
    ws, wsRef,
    setMessages,
    showMentionDropdown, setShowMentionDropdown,
    setMentionResults,
    showEmojiPicker, setShowEmojiPicker,
    showGiphyPicker, setShowGiphyPicker,
    giphySearch, setGiphySearch,
    giphyResults, setGiphyResults,
    uploadingFile, setUploadingFile,
    fileInputRef,
    selectedFriend
  } = state;

  const handleMessageInputChange = useCallback((e) => {
    const value = e.target.value;
    setMessageInput(value);

    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1 && lastAtIndex === value.length - 1) {
      setMentionResults(members.slice(0, 5));
      setShowMentionDropdown(true);
    } else if (lastAtIndex !== -1) {
      const afterAt = value.substring(lastAtIndex + 1);
      if (!afterAt.includes(' ')) {
        const filtered = members.filter(m => 
          m.username?.toLowerCase().startsWith(afterAt.toLowerCase())
        ).slice(0, 5);
        setMentionResults(filtered);
        setShowMentionDropdown(filtered.length > 0);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  }, [setMessageInput, members, setMentionResults, setShowMentionDropdown]);

  const selectMention = useCallback((member) => {
    const lastAtIndex = messageInput.lastIndexOf('@');
    const beforeAt = messageInput.substring(0, lastAtIndex);
    const newInput = beforeAt + '@' + member.username + ' ';
    setMessageInput(newInput);
    setShowMentionDropdown(false);
  }, [messageInput, setMessageInput, setShowMentionDropdown]);

  const sendMessage = useCallback(() => {
    if (!messageInput.trim() || !selectedRoom) return;

    const optimisticId = `temp-${Date.now()}`;
    const optimisticMessage = {
      id: optimisticId,
      room_id: selectedRoom.id,
      content: messageInput,
      sender_id: user.id,
      sender: {
        id: user.id,
        username: user.username,
        display_name: user.display_name,
        avatar_url: user.avatar_url,
        role: user.role,
        badges: user.badges || [],
        badge_details: user.badge_details || []
      },
      created_at: new Date().toISOString(),
      reply_to: replyingTo?.id || null,
      parent_message: replyingTo || null,
      mentions: [], // Will be populated by backend, but kept for optimistic UI
      _optimistic: true,
      _timestamp: Date.now()
    };

    setMessages(prev => [...prev, optimisticMessage]);

    const message = {
      type: 'message',
      room_id: selectedRoom.id,
      content: messageInput,
      reply_to: replyingTo?.id || null,
      // `mentions` are now handled by the backend
      tempId: optimisticId
    };

    console.time('MessageLag');

    const currentWs = wsRef.current || ws;
    if (currentWs && currentWs.readyState === WebSocket.OPEN) {
      currentWs.send(JSON.stringify(message));
    } else {
      messageQueue.enqueue(message);
      toast.info('Message queued - will send when connection is restored');
    }

    setMessageInput('');
    setReplyingTo(null);
    setShowMentionDropdown(false);
  }, [messageInput, selectedRoom, replyingTo, user, ws, wsRef, setMessages, setMessageInput, setReplyingTo, setShowMentionDropdown]);

  const onEmojiClick = useCallback((emojiObject) => {
    // Send emoji immediately instead of adding to input
    const currentWs = wsRef.current || ws;
    if (currentWs && currentWs.readyState === WebSocket.OPEN && selectedRoom) {
      const optimisticId = `temp-emoji-${Date.now()}`;
      const optimisticMessage = {
        id: optimisticId,
        room_id: selectedRoom.id,
        content: emojiObject.emoji,
        sender_id: user.id,
        sender: {
          id: user.id,
          username: user.username,
          display_name: user.display_name,
          avatar_url: user.avatar_url,
          role: user.role,
          badges: user.badges || [],
          badge_details: user.badge_details || []
        },
        created_at: new Date().toISOString(),
        _optimistic: true,
        _timestamp: Date.now()
      };
      setMessages(prev => [...prev, optimisticMessage]);
      
      currentWs.send(JSON.stringify({
        type: 'message',
        room_id: selectedRoom.id,
        content: emojiObject.emoji,
        reply_to: null,
        mentions: [],
        tempId: optimisticId
      }));
    }
    setShowEmojiPicker(false);
  }, [ws, wsRef, selectedRoom, user, setMessages, setShowEmojiPicker]);

  const searchGiphy = useCallback(async (query) => {
    const klipyApiKey = process.env.REACT_APP_KLIPY_API_KEY;
    if (!klipyApiKey) {
      console.error('Klipy API key not configured');
      toast.error('GIF search not available');
      return;
    }
    
    try {
      let response;
      if (!query || !query.trim()) {
        // Load trending GIFs if no query
        response = await axios.get(`https://api.klipy.com/api/v1/${klipyApiKey}/gifs/trending`, {
          params: { limit: 16 }
        });
      } else {
        // Search GIFs with query
        response = await axios.get(`https://api.klipy.com/api/v1/${klipyApiKey}/gifs/search`, {
          params: { q: query, limit: 16 }
        });
      }
      
      const gifs = response.data.data?.data?.map(gif => ({
        id: gif.id || gif.slug,
        images: {
          fixed_height_small: { url: gif.file?.sm?.gif?.url || gif.file?.md?.gif?.url },
          original: { url: gif.file?.hd?.gif?.url || gif.file?.md?.gif?.url }
        },
        title: gif.title || ''
      })) || [];
      setGiphyResults(gifs);
    } catch (error) {
      console.error('GIF search failed:', error);
      toast.error('Failed to load GIFs');
    }
  }, [setGiphyResults]);

  const selectGif = useCallback((gifUrl) => {
    const currentWs = wsRef.current || ws;
    if (currentWs && currentWs.readyState === WebSocket.OPEN && selectedRoom) {
      currentWs.send(JSON.stringify({
        type: 'message',
        room_id: selectedRoom.id,
        content: gifUrl,
        reply_to: null,
        mentions: []
      }));
      toast.success('GIF sent!');
    }
    setShowGiphyPicker(false);
    setGiphyResults([]);
    setGiphySearch('');
  }, [ws, wsRef, selectedRoom, setShowGiphyPicker, setGiphyResults, setGiphySearch]);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File size exceeds limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/media/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const mediaUrl = getImageUrl(response.data.url);
      const currentWs = wsRef.current || ws;
      
      if (selectedFriend) {
        currentWs.send(JSON.stringify({
          type: 'pm',
          recipient_id: selectedFriend.id,
          content: mediaUrl,
          msg_type: 'media'
        }));
      } else if (selectedRoom) {
        currentWs.send(JSON.stringify({
          type: 'message',
          room_id: selectedRoom.id,
          content: mediaUrl,
        }));
      }
      
      toast.success('Media uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [ws, wsRef, selectedRoom, selectedFriend, setUploadingFile, fileInputRef]);

  const handleAvatarClick = useCallback((username, senderId) => {
    if (senderId === user.id) return;
    
    setMessageInput(prev => {
      const mention = `@${username} `;
      if (prev.length === 0) return mention;
      if (prev.endsWith(' ')) return prev + mention;
      return prev + ' ' + mention;
    });
  }, [user.id, setMessageInput]);

  // Dedicated function for sending emoji directly (used by ChatArea component)
  const sendEmoji = useCallback((emoji) => {
    const currentWs = wsRef.current || ws;
    if (currentWs && currentWs.readyState === WebSocket.OPEN && selectedRoom) {
      const optimisticId = `temp-emoji-${Date.now()}`;
      const optimisticMessage = {
        id: optimisticId,
        room_id: selectedRoom.id,
        content: emoji,
        sender_id: user.id,
        sender: {
          id: user.id,
          username: user.username,
          display_name: user.display_name,
          avatar_url: user.avatar_url,
          role: user.role,
          badges: user.badges || [],
          badge_details: user.badge_details || []
        },
        created_at: new Date().toISOString(),
        _optimistic: true,
        _timestamp: Date.now()
      };
      setMessages(prev => [...prev, optimisticMessage]);
      
      currentWs.send(JSON.stringify({
        type: 'message',
        room_id: selectedRoom.id,
        content: emoji,
        reply_to: null,
        mentions: [],
        tempId: optimisticId
      }));
    }
  }, [ws, wsRef, selectedRoom, user, setMessages]);

  return {
    handleMessageInputChange,
    selectMention,
    sendMessage,
    sendEmoji,
    onEmojiClick,
    searchGiphy,
    selectGif,
    handleFileUpload,
    handleAvatarClick
  };
};

export default useMessageHandlers;
